import { useMemo } from 'react'
import { useFetchPublicCatalogObjectivesQuery } from '@/store/api/publicCatalogApi'
import { COLLECTION_NO_VALUE } from '@/store/api/showcasesApi'
import { useAppSelector } from '@/store/hooks'
import { createFiltersSelector } from '@/store/slices/aggregatesSlice'

export const usePublicCatalogObjectives = () => {
  const { data: { data: catalogObjectives = [], totalCount: totalCatalogObjectives = 0 } = {}, isLoading } =
    useFetchPublicCatalogObjectivesQuery()

  const collectionFilters = useAppSelector((state) => createFiltersSelector(state, 'public').collection)
  const searchText = useAppSelector((state) => state.aggregates.catalogCoursesSearchText)

  const filteredCatalogObjectives = useMemo(() => {
    if (!collectionFilters.length) {
      return catalogObjectives
    }

    const hasNoCollection = collectionFilters.includes(COLLECTION_NO_VALUE)

    return catalogObjectives.filter(({ showcases = [] }) => {
      const collectionIds = showcases.map(({ id }) => id)

      if (hasNoCollection) {
        return collectionIds.length === 0 || collectionIds.some((id) => collectionFilters.includes(id))
      }

      return collectionIds.some((id) => collectionFilters.includes(id))
    })
  }, [catalogObjectives, collectionFilters])

  const searchedCatalogObjectives = useMemo(() => {
    const trimmedSearchText = searchText?.trim().toLowerCase()

    if (!trimmedSearchText) {
      return filteredCatalogObjectives
    }

    return filteredCatalogObjectives.filter(({ name, code, description }) =>
      [name, code, description].some((field) => field?.toLowerCase().includes(trimmedSearchText)),
    )
  }, [searchText, filteredCatalogObjectives])

  const uncategorizedCoursesCount = catalogObjectives.filter((course) => !course.showcases?.length).length
  const hasNoShowCase =
    collectionFilters.includes(COLLECTION_NO_VALUE) ||
    (uncategorizedCoursesCount > 0 && uncategorizedCoursesCount !== catalogObjectives.length)

  return {
    catalogObjectives,
    searchedCatalogObjectives,
    filteredCatalogObjectives,
    totalCatalogObjectives,
    isLoading,
    hasNoShowCase,
  }
}
